import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Dialog,
  TextField,
  Box,
  Button,
  DialogActions,
  Tooltip,
} from "@mui/material";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import { getStyles } from "src/styles/theme";
import defaultStyles from "./styles";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getImages,
  updateProgressDetails,
  deleteProgressDetails,
  getTargetQueries,
} from "src/utils/api/dashboard";
import { useState } from "react";
import Snackbar from "src/components/common/Snackbar";
import { SnackbarType } from "src/types/common";

export type DashBoardTable = {
  topHeader: string[];
  data: any;
  targetId: string | number;
  // targetUpdatedAt?: string;
};

export function ProgressHistory({
  data,
  topHeader,
  targetId,
}: // targetUpdatedAt,
DashBoardTable) {
  const styles = getStyles(defaultStyles);
  const queryClient = useQueryClient();

  const [selectedRow, setSelectedRow] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<any>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [showSnackbar, setShowSnackbar] = useState<SnackbarType>({
    severity: "success",
    message: "",
  });
  const [editErrors, setEditErrors] = useState<Record<string, boolean>>({});

  const targetUpdatedAt = data && data.length > 0 ? data[0].updated_at : null;

  // Fetch queries for this target to check if there are any admin queries
  const { data: queriesData, refetch: refetchQueries } = useQuery(
    ["targetQueries", targetId],
    async () => {
      try {
        const response = await getTargetQueries(String(targetId));
        const data = response?.data?.data || [];
        // Convert object with numeric keys to array
        if (data && typeof data === "object" && !Array.isArray(data)) {
          return Object.values(data);
        }
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error fetching queries:", error);
        return [];
      }
    },
    {
      enabled: !!targetId,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  // Check if there are any admin queries created after the target was last updated
  const disableActions = (() => {
    if (!targetUpdatedAt) {
      return true; // No target update time, lock actions
    }

    if (!queriesData || queriesData.length === 0) {
      return true; // No queries, lock actions
    }

    const targetDate = new Date(targetUpdatedAt);

    // Find all admin queries
    const adminQueries = queriesData.filter((query: any) => {
      const isAdmin =
        query.userSubType === "ADMIN" || query.user?.userType === "ADMIN";
      return isAdmin;
    });

    if (adminQueries.length === 0) {
      return true; // No admin queries, lock actions
    }

    // Get the LAST (most recent) admin query
    const sortedAdminQueries = adminQueries.sort((a: any, b: any) => {
      const dateA = new Date(a.created_at || a.createdAt);
      const dateB = new Date(b.created_at || b.createdAt);
      return dateB.getTime() - dateA.getTime(); // Sort descending (newest first)
    });

    const lastAdminQuery = sortedAdminQueries[0];
    const lastAdminQueryDate = new Date(
      lastAdminQuery.created_at || lastAdminQuery.createdAt
    );

    const isAdminQueryAfterTargetUpdate = lastAdminQueryDate > targetDate;

    // If last admin query is after target update → UNLOCK (admin wants changes)
    // If last admin query is before/equal target update → LOCK (waiting for admin review)
    return !isAdminQueryAfterTargetUpdate;
  })();

  const { data: photosData, isLoading } = useQuery(
    ["fetchPhotos", selectedRow],
    () => getImages(selectedRow), // API call to fetch photos for the selected row
    {
      enabled: !!selectedRow, // Only run when a row is selected
    }
  );

  const handleOpenDialog = (row: any) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const updateProgressMutation = useMutation(
    ({ id, data }: { id: string; data: any }) =>
      updateProgressDetails(id, data),
    {
      onSuccess: () => {
        setShowSnackbar({
          severity: "success",
          message: "Progress updated successfully",
        });
        setEditingRowId(null);
        setEditFormData({});
        queryClient.invalidateQueries();
        // Refetch queries to check lock status again
        refetchQueries();
      },
      onError: (error: any) => {
        setShowSnackbar({
          severity: "error",
          message:
            error?.response?.data?.message || "Failed to update progress",
        });
      },
    }
  );

  const handleEditClick = (row: any) => {
    setEditingRowId(row.id);
    setEditFormData({
      progressDate: row.progressDate ? row.progressDate.split("T")[0] : "",
      fromDate: row.fromDate ? row.fromDate.split("T")[0] : "",
      toDate: row.toDate ? row.toDate.split("T")[0] : "",
      quantityInNumber: row.quantityInNumber || 0,
      cost: row.cost || 0,
      totalManDays: row.totalManDays || 0,
    });
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditFormData({});
    setEditErrors({});
  };

  const handleSaveEdit = (id: string) => {
    const requiredFields = [
      "progressDate",
      "fromDate",
      "toDate",
      "quantityInNumber",
      "cost",
      "totalManDays",
    ];
    const errors: Record<string, boolean> = {};
    requiredFields.forEach((field) => {
      if (
        editFormData[field] === undefined ||
        editFormData[field] === null ||
        editFormData[field] === ""
      ) {
        errors[field] = true;
      }
    });
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      setShowSnackbar({
        severity: "error",
        message: "All fields are mandatory. Please fill in all fields.",
      });
      return;
    }
    setEditErrors({});
    const dataToSend = {
      ...editFormData,
      quantityInNumber: Number(editFormData.quantityInNumber),
      cost: Number(editFormData.cost),
      totalManDays: Number(editFormData.totalManDays),
    };
    updateProgressMutation.mutate({ id, data: dataToSend });
  };

  const handleInputChange = (field: string, value: any) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (value !== "" && value !== null && value !== undefined) {
      setEditErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleResetSnackbar = () => {
    setShowSnackbar({ severity: "success", message: "" });
  };

  const deleteProgressMutation = useMutation(
    (id: string) => deleteProgressDetails(id),
    {
      onSuccess: () => {
        setShowSnackbar({
          severity: "success",
          message: "Progress deleted successfully",
        });
        setDeleteDialogOpen(false);
        setDeleteTargetId(null);
        queryClient.invalidateQueries();
        // Refetch queries to check lock status again
        refetchQueries();
      },
      onError: (error: any) => {
        setShowSnackbar({
          severity: "error",
          message:
            error?.response?.data?.message || "Failed to delete progress",
        });
        setDeleteDialogOpen(false);
        setDeleteTargetId(null);
      },
    }
  );

  const handleDeleteClick = (id: string) => {
    setDeleteTargetId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteTargetId) {
      deleteProgressMutation.mutate(deleteTargetId);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  };

  const dateFormatter = (date: string) => {
    const newDate = new Date(date.split("T")[0]);
    return newDate.toLocaleDateString("en-GB");
  };

  return (
    <TableContainer component={Paper} sx={{ height: "fit-content" }}>
      <Table sx={{ minWidth: 600 }} aria-label="simple table">
        <TableHead {...styles("header")}>
          <TableRow>
            {topHeader.map((item, index) => (
              <TableCell align="center" {...styles("cell")} key={index}>
                {item}
              </TableCell>
            ))}
            <TableCell align="center" {...styles("cell")}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((seedData, index) => {
            if (!seedData) return null;
            const isEditing = editingRowId === seedData.id;

            return (
              <TableRow key={index}>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "100px" })}
                >
                  {isEditing ? (
                    <TextField
                      type="date"
                      size="small"
                      required
                      value={editFormData.progressDate || ""}
                      onChange={(e) =>
                        handleInputChange("progressDate", e.target.value)
                      }
                      error={!!editErrors.progressDate}
                      helperText={editErrors.progressDate ? "Required" : ""}
                      InputLabelProps={{ shrink: true }}
                    />
                  ) : seedData.progressDate ? (
                    dateFormatter(seedData.progressDate)
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "100px" })}
                >
                  {isEditing ? (
                    <TextField
                      type="date"
                      size="small"
                      required
                      value={editFormData.fromDate || ""}
                      onChange={(e) =>
                        handleInputChange("fromDate", e.target.value)
                      }
                      error={!!editErrors.fromDate}
                      helperText={editErrors.fromDate ? "Required" : ""}
                      InputLabelProps={{ shrink: true }}
                    />
                  ) : seedData.fromDate ? (
                    dateFormatter(seedData.fromDate)
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "100px" })}
                >
                  {isEditing ? (
                    <TextField
                      type="date"
                      size="small"
                      required
                      value={editFormData.toDate || ""}
                      onChange={(e) =>
                        handleInputChange("toDate", e.target.value)
                      }
                      error={!!editErrors.toDate}
                      helperText={editErrors.toDate ? "Required" : ""}
                      InputLabelProps={{ shrink: true }}
                    />
                  ) : seedData.toDate ? (
                    dateFormatter(seedData.toDate)
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "100px" })}
                >
                  {isEditing ? (
                    <TextField
                      type="number"
                      size="small"
                      required
                      value={editFormData.quantityInNumber ?? ""}
                      onChange={(e) =>
                        handleInputChange("quantityInNumber", e.target.value)
                      }
                      error={!!editErrors.quantityInNumber}
                      helperText={editErrors.quantityInNumber ? "Required" : ""}
                    />
                  ) : (
                    seedData.quantityInNumber
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "100px" })}
                >
                  {isEditing ? (
                    <TextField
                      type="number"
                      size="small"
                      required
                      value={editFormData.cost ?? ""}
                      onChange={(e) =>
                        handleInputChange("cost", e.target.value)
                      }
                      error={!!editErrors.cost}
                      helperText={editErrors.cost ? "Required" : ""}
                    />
                  ) : (
                    seedData.cost
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "100px" })}
                >
                  {isEditing ? (
                    <TextField
                      type="number"
                      size="small"
                      required
                      value={editFormData.totalManDays ?? ""}
                      onChange={(e) =>
                        handleInputChange("totalManDays", e.target.value)
                      }
                      error={!!editErrors.totalManDays}
                      helperText={editErrors.totalManDays ? "Required" : ""}
                    />
                  ) : (
                    seedData.totalManDays
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "70px" })}
                >
                  <IconButton
                    aria-label="view photos"
                    onClick={() => handleOpenDialog(seedData.id)}
                  >
                    <ImageSearchIcon />
                  </IconButton>
                </TableCell>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "120px" })}
                >
                  {isEditing ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        aria-label="save"
                        color="primary"
                        onClick={() => handleSaveEdit(seedData.id)}
                        disabled={updateProgressMutation.isLoading}
                      >
                        <SaveIcon />
                      </IconButton>
                      <IconButton
                        aria-label="cancel"
                        color="secondary"
                        onClick={handleCancelEdit}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip
                        title={
                          disableActions
                            ? "Actions locked due to pending admin queries"
                            : "Edit progress"
                        }
                      >
                        <span>
                          <IconButton
                            aria-label="edit"
                            color="primary"
                            onClick={() => handleEditClick(seedData)}
                            disabled={disableActions}
                            sx={{ opacity: disableActions ? 0.5 : 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          disableActions
                            ? "Actions locked due to pending admin queries"
                            : "Delete progress"
                        }
                      >
                        <span>
                          <IconButton
                            aria-label="delete"
                            color="error"
                            onClick={() => handleDeleteClick(seedData.id)}
                            disabled={disableActions}
                            sx={{ opacity: disableActions ? 0.5 : 1 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Dialog open={open} onClose={handleCloseDialog}>
        <DialogTitle>GeoTagged Photos</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <CircularProgress />
          ) : photosData && photosData.data.data.length > 0 ? (
            <div>
              {photosData.data.data.map((photo: any, idx: number) => (
                <img
                  key={idx}
                  src={photo.url}
                  alt={photo.key}
                  style={{ width: "100%", marginBottom: "20px" }}
                />
              ))}
            </div>
          ) : (
            <p>No GeoTagged Photos Available</p>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this progress record? This action
          cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteProgressMutation.isLoading}
          >
            {deleteProgressMutation.isLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!showSnackbar.message}
        message={showSnackbar.message}
        severity={showSnackbar.severity}
        resetSnackbar={handleResetSnackbar}
      />
    </TableContainer>
  );
}
